// src/store/customer/useCustomerPaymentStore.js
import { create } from "zustand";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import { toast } from "react-hot-toast";
import useAuthStore from "../useAuthStore";

const useCustomerPaymentStore = create((set, get) => ({
    // Builder's Payment Requests (Customer views and responds to these)
    paymentRequests: [],
    isRequestsLoading: false, // Loading for fetching the list of requests
    isActionSubmitting: false, // Loading when customer accepts/declines a request

    fetchPaymentRequestsForCustomer: async (projectId) => {
        if (!projectId) {
            set({ paymentRequests: [], isRequestsLoading: false });
            return;
        }
        set({ isRequestsLoading: true });
        const { token } = useAuthStore.getState();
        if (!token) {
            toast.error("Authentication required to fetch payment requests.");
            set({ isRequestsLoading: false });
            return;
        }
        try {
            // All API references below should use the central axios instance from src/lib/axiosInstance.js
            // Example usage:
            //    import api from '../../lib/axiosInstance';
            //    api.get(`/projects/${projectId}/builder-payment-requests`)
            // No direct /api/v1/ or localhost references
            const response = await axiosInstancev1.get(
                `/projects/${projectId}/builder-payment-requests`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                // Sort by request_date descending for most recent first
                const sortedRequests = (response.data.data || []).sort((a, b) => new Date(b.request_date) - new Date(a.request_date));
                console.log("API Response - Payment Requests:", response.data.data);
                console.log("Sorted Payment Requests:", sortedRequests);
                set({ paymentRequests: sortedRequests, isRequestsLoading: false });
            } else {
                throw new Error(response.data.message || "Failed to fetch payment requests.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Could not fetch payment requests.";
            toast.error(errorMsg);
            set({ paymentRequests: [], isRequestsLoading: false });
        }
    },

    respondToPaymentRequest: async (projectId, requestId, action, reason = "") => {
        set({ isActionSubmitting: true });
        const { token } = useAuthStore.getState();
        if (!token) {
            toast.error("Authentication required.");
            set({ isActionSubmitting: false });
            return { success: false, message: "Authentication required." };
        }
        try {
            // All API references below should use the central axios instance from src/lib/axiosInstance.js
            // Example usage:
            //    import api from '../../lib/axiosInstance';
            //    api.put(`/projects/${projectId}/builder-payment-requests/${requestId}/respond`)
            // No direct /api/v1/ or localhost references
            const payload = { action };
            if (action === "decline") {
                payload.customer_reason = reason;
            }

            const response = await axiosInstancev1.put(
                `/projects/${projectId}/builder-payment-requests/${requestId}/respond`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                toast.success(response.data.message || `Request ${action === 'accept' ? 'approved' : 'declined'}.`);
                // Refresh the list of payment requests after action
                get().fetchPaymentRequestsForCustomer(projectId);
                return { success: true, message: response.data.message, data: response.data.data };
            } else {
                throw new Error(response.data.message || `Failed to ${action} request.`);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || `Could not ${action} request.`;
            toast.error(errorMsg);
            return { success: false, message: errorMsg };
        } finally {
            set({ isActionSubmitting: false });
        }
    },

    // --- MODIFICATION START ---
    respondToValueEntry: async (projectId, entryId, newStatus, comment = "") => {
        set({ isActionSubmitting: true }); // Reuse the same loading state for simplicity
        const { token } = useAuthStore.getState();
        if (!token) {
            toast.error("Authentication required.");
            set({ isActionSubmitting: false });
            return { success: false };
        }
        try {
            const payload = { status: newStatus, comment };
            const response = await axiosInstancev1.put(
                `/projects/${projectId}/value-entries/${entryId}`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                toast.success(response.data.message);
                // Refresh the list to show the updated status
                get().fetchProjectValueEntriesForCustomer(projectId);
                return { success: true };
            } else {
                throw new Error(response.data.message || "Failed to update value entry status.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Could not update value entry.";
            toast.error(errorMsg);
            return { success: false, message: errorMsg };
        } finally {
            set({ isActionSubmitting: false });
        }
    },
    // --- MODIFICATION END ---

    // Project Value History (Customer views approved entries from Builder)
    projectValueEntries: [],
    isValueEntriesLoading: false,
    fetchProjectValueEntriesForCustomer: async (projectId) => {
        if (!projectId) {
            set({ projectValueEntries: [], isValueEntriesLoading: false });
            return;
        }
        set({ isValueEntriesLoading: true });
        const { token } = useAuthStore.getState();
        if (!token) {
            toast.error("Authentication required to fetch project budget history.");
            set({ isValueEntriesLoading: false });
            return;
        }
        try {
            const response = await axiosInstancev1.get(
                `/projects/${projectId}/value-entries`, // Same endpoint as builder
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                // MODIFIED: Removed the filter to show all statuses (including Pending) to the customer.
                const sortedAndFilteredEntries = (response.data.data || [])
                    .sort((a, b) => {
                        const dateComparison = new Date(b.entry_date).getTime() - new Date(a.entry_date).getTime();
                        if (dateComparison !== 0) return dateComparison;
                        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                    });
                set({ projectValueEntries: sortedAndFilteredEntries, isValueEntriesLoading: false });
            } else {
                throw new Error(response.data.message || "Failed to fetch project budget history.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Could not fetch project budget history.";
            toast.error(errorMsg);
            set({ projectValueEntries: [], isValueEntriesLoading: false });
        }
    },

    // Customer's Own Transaction History
    transactions: [],
    isTransactionsLoading: false, // Specific loading for customer's actual payment history
    fetchTransactionHistoryForCustomer: async (projectId) => {
        if (!projectId) {
            set({ transactions: [], isTransactionsLoading: false });
            return;
        }
        set({ isTransactionsLoading: true });
        const { token } = useAuthStore.getState();
        if (!token) {
            toast.error("Authentication required to fetch transaction history.");
            set({ isTransactionsLoading: false });
            return;
        }
        try {
            // This endpoint should fetch actual payments made by the customer for this project
            const response = await axiosInstancev1.get(
                `/projects/${projectId}/transactions`, // Assumes this endpoint returns customer-specific transactions
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                // Sort by transaction_date descending for most recent first
                const sortedTransactions = (response.data.data || []).sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date));
                set({ transactions: sortedTransactions, isTransactionsLoading: false });
            } else {
                throw new Error(response.data.message || "Failed to fetch transaction history.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Could not fetch transaction history.";
            toast.error(errorMsg);
            set({ transactions: [], isTransactionsLoading: false });
        }
    },

    // Refresh all data for a project
    refreshProjectData: async (projectId) => {
        if (!projectId) return;
        
        // Refresh all data in parallel
        await Promise.all([
            get().fetchPaymentRequestsForCustomer(projectId),
            get().fetchProjectValueEntriesForCustomer(projectId),
            get().fetchTransactionHistoryForCustomer(projectId)
        ]);
    },
}));

export default useCustomerPaymentStore;